import { getStaffSessionToken } from '../staffSession';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import logo from '../../../assets/images/farmstream_logo.png';
import { authSelector } from '../state/authSlice';
import {
  useSimHealthReportMutation,
  useOrphanSimActionMutation,
  useOveruseEmailPreviewMutation,
  useOveruseTestEmailsMutation,
  useOveruseDunningStatusMutation,
  useRunOveruseDunningMutation,
  useSetOveruseScopeMutation
} from '../api/authApi';
import { Button } from '../../../components/Button';

type HealthKey = 'orphans' | 'active' | 'unpaid' | 'barred-no-pause';
type ReportKey = HealthKey | 'usage' | 'dunning';

type HealthRow = {
  iccid: string;
  email: string;
  stripe_id: string;
  plan: string;
  operator_name: string;
  tariff_name: string;
  connection_date: string;
  is_barred: boolean;
  note: string;
  alt_stripe_id: string;
};

type UsageRow = {
  iccid: string;
  email: string;
  device: string;
  operator_name: string;
  tariff_name: string;
  sim_state: string;
  plan_gb: number;
  used_gb: number;
  projected_gb: number;
  pct_used: number | null;
  over_gb: number;
  over_cost_gbp: number;
  note: string;
};

type Tile = { label: string; value: number; highlight?: boolean };

type ReportSection = {
  tiles: Tile[];
  rows: HealthRow[];
};

type UsageSection = {
  tiles: Tile[];
  rows: UsageRow[];
};

type AllReports = {
  generated_at: string;
  reports: Record<HealthKey, ReportSection> & { usage: UsageSection };
};

const REPORTS: Array<{ key: ReportKey; label: string; description: string }> = [
  {
    key: 'orphans',
    label: 'Orphan SIMs',
    description: 'Live in Pelion but missing from the database — potentially not being paid for.'
  },
  {
    key: 'active',
    label: 'Active fleet',
    description: 'Every live SIM — not barred, not terminated — with its customer context.'
  },
  {
    key: 'unpaid',
    label: 'Unpaid SIMs',
    description:
      'Live, unbarred SIMs with a database row but no active/trialing/past-due Stripe subscription (verified live against Stripe).'
  },
  {
    key: 'barred-no-pause',
    label: 'Barred without pause',
    description: 'Barred in Pelion with no active pause on record — SIMs stuck in limbo.'
  },
  {
    key: 'usage',
    label: 'Usage & overuse',
    description:
      'Month-to-date data per billed SIM with projected end-of-month vs plan allowance — biggest overusers first (plans reset on the 1st).'
  },
  {
    key: 'dunning',
    label: 'Dunning pipeline',
    description:
      'Where each of the worst overusers is in the warn → 7-day check → bar cycle this month.'
  }
];

// Big reports (the active fleet is ~3,500 rows) render the first N rows;
// the CSV download always contains everything.
const DISPLAY_CAP = 500;

/**
 * Team-only (/callum) subscriber-base health reports, reached after Google
 * Workspace SSO via /team-login. Cross-references Pelion, the activations
 * database and Stripe.
 */
export const SimHealth = () => {
  const navigate = useNavigate();
  const { isAdmin, token } = useSelector(authSelector);
  const [runReport, { isLoading }] = useSimHealthReportMutation();
  const [simAction] = useOrphanSimActionMutation();

  const [selected, setSelected] = useState<ReportKey>('orphans');
  // One API call computes every report; pill clicks just switch views.
  const [data, setData] = useState<AllReports | null>(null);
  // Usage view: focus on H3G Ireland SIMs, which carry direct per-GB cost.
  const [h3gOnly, setH3gOnly] = useState(false);
  // Dunning email preview (usage view)
  const [fetchEmailPreview, { data: emailPreview, isLoading: loadingPreview }] =
    useOveruseEmailPreviewMutation();
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<'stage1' | 'stage2' | 'barred' | 'reset'>('stage1');
  const [sendTestEmails, { isLoading: sendingTests }] = useOveruseTestEmailsMutation();

  const onSendTestEmails = async () => {
    if (!staffSession) return;
    try {
      const res = await sendTestEmails({  }).unwrap();
      toast.success(`Test copies of ${res.sent.length} emails sent to ${res.recipient}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to send test emails');
    }
  };
  // Dunning pipeline dashboard
  const [fetchDunningStatus, { data: dunningStatus, isLoading: loadingDunning }] =
    useOveruseDunningStatusMutation();
  const [runDunning, { isLoading: runningDunning }] = useRunOveruseDunningMutation();
  const [setScope, { isLoading: settingScope }] = useSetOveruseScopeMutation();

  const onSetScope = async (scope: 'h3g' | 'all') => {
    if (!staffSession || dunningStatus?.config.scope === scope) return;
    try {
      await setScope({  scope }).unwrap();
      toast.success(
        scope === 'h3g' ? 'Dunning now targets H3G SIMs only' : 'Dunning now targets ALL SIMs'
      );
      refreshDunning();
    } catch (err) {
      console.error(err);
      toast.error('Failed to change scope');
    }
  };

  const refreshDunning = () => {
    if (staffSession) fetchDunningStatus({  });
  };

  const onDryRun = async (mode: 'warn' | 'enforce') => {
    if (!staffSession) return;
    try {
      const res = await runDunning({  mode }).unwrap();
      const summary = Object.entries(res.counts)
        .filter(([, n]) => n > 0)
        .map(([t, n]) => `${t}: ${n}`)
        .join(' · ');
      toast.info(`Dry run (${mode}) — ${summary || 'nothing to do'}`);
      refreshDunning();
    } catch (err) {
      console.error(err);
      toast.error(`Dry run (${mode}) failed`);
    }
  };

  const onDashboardBar = async (iccid: string) => {
    if (!staffSession) return;
    try {
      const res = await simAction({  iccid, action: 'bar' }).unwrap();
      toast.success(res.message || `Barred ${iccid}`);
      refreshDunning();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || `Failed to bar ${iccid}`);
    }
  };
  // ICCID of the row whose action is in flight, and which terminate is armed
  const [busyIccid, setBusyIccid] = useState<string | null>(null);
  const [armedTerminate, setArmedTerminate] = useState<string | null>(null);

  const staffSession = useMemo(() => getStaffSessionToken() || '', []);

  // Without an admin session, bounce to team-login (Google Workspace SSO);
  // ?next brings them straight back here after signing in.
  useEffect(() => {
    if (!token || !isAdmin || !staffSession) {
      navigate('/team-login?next=/callum', { replace: true });
    }
  }, [token, isAdmin, staffSession, navigate]);

  const onRun = async () => {
    if (!staffSession) return;
    setData(null);
    setArmedTerminate(null);
    try {
      const res = await runReport({  }).unwrap();
      setData({ generated_at: res.generated_at, reports: res.reports });
    } catch (err) {
      console.error(err);
      toast.error('Report failed — check the backend logs and try again.');
    }
  };

  const onSimAction = async (iccid: string, action: 'bar' | 'unbar' | 'terminate') => {
    if (!staffSession || busyIccid) return;

    // Terminate is irreversible — require a second click to confirm.
    if (action === 'terminate' && armedTerminate !== iccid) {
      setArmedTerminate(iccid);
      setTimeout(() => setArmedTerminate((cur) => (cur === iccid ? null : cur)), 4000);
      return;
    }
    setArmedTerminate(null);

    setBusyIccid(iccid);
    try {
      const res = await simAction({  iccid, action }).unwrap();
      toast.success(res.message || `${action} applied to ${iccid}`);
      // A SIM can appear in several reports — update the cached rows of all
      // health sections (the usage report has its own row shape and no actions).
      setData((prev) => {
        if (!prev) return prev;
        const healthKeys: HealthKey[] = ['orphans', 'active', 'unpaid', 'barred-no-pause'];
        const updated = { ...prev.reports };
        healthKeys.forEach((key) => {
          const section = prev.reports[key];
          updated[key] = {
            ...section,
            rows:
              action === 'terminate'
                ? section.rows.filter((r) => r.iccid !== iccid)
                : section.rows.map((r) =>
                    r.iccid === iccid ? { ...r, is_barred: action === 'bar' } : r
                  )
          };
        });
        return { ...prev, reports: updated };
      });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || `Failed to ${action} ${iccid}`);
    } finally {
      setBusyIccid(null);
    }
  };

  // Load the dunning pipeline when its pill is first selected.
  useEffect(() => {
    if (selected === 'dunning' && staffSession && !dunningStatus) {
      fetchDunningStatus({  });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const usageSelected = selected === 'usage';
  const healthSection: ReportSection | null =
    data && !usageSelected && selected !== 'dunning'
      ? data.reports[selected as HealthKey]
      : null;
  const usageSection: UsageSection | null = data && usageSelected ? data.reports.usage : null;

  // Usage rows honour the H3G filter; the count tiles are recomputed from
  // the filtered set so they stay truthful (month-elapsed comes from the
  // backend snapshot).
  const usageRows: UsageRow[] = usageSection
    ? usageSection.rows.filter(
        (r) => !h3gOnly || r.operator_name.toLowerCase().includes('h3g')
      )
    : [];
  const usageTiles: Tile[] = usageSection
    ? (() => {
        const already = usageRows.filter((r) => r.plan_gb > 0 && r.used_gb > r.plan_gb).length;
        const projected = usageRows.filter(
          (r) => r.plan_gb > 0 && r.used_gb <= r.plan_gb && r.projected_gb > r.plan_gb
        ).length;
        const overCost = usageRows.reduce((sum, r) => sum + r.over_cost_gbp, 0);
        const monthTile = usageSection.tiles.find((t) =>
          t.label.toLowerCase().includes('elapsed')
        );
        return [
          {
            label: h3gOnly ? 'H3G billed SIMs' : 'Billed SIMs tracked',
            value: usageRows.length
          },
          { label: 'Already over plan', value: already, highlight: already > 0 },
          { label: 'Projected to exceed plan', value: projected, highlight: projected > 0 },
          {
            label: 'Projected overuse cost £/mo',
            value: Math.round(overCost),
            highlight: overCost > 0
          },
          ...(monthTile ? [monthTile] : [])
        ];
      })()
    : [];

  const current: { tiles: Tile[]; rows: Array<HealthRow | UsageRow> } | null = usageSection
    ? { tiles: usageTiles, rows: usageRows }
    : healthSection;

  const escapeCsv = (v: string | number | boolean | null) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const downloadCsv = () => {
    if (!current) return;
    let lines: string[];
    if (usageSection) {
      lines = [
        'iccid,email,device,operator_name,tariff_name,sim_state,plan_gb,used_gb,projected_gb,pct_used,over_gb,over_cost_gbp,note',
        ...usageRows.map((r) =>
          [
            r.iccid,
            r.email,
            r.device,
            r.operator_name,
            r.tariff_name,
            r.sim_state,
            r.plan_gb,
            r.used_gb,
            r.projected_gb,
            r.pct_used,
            r.over_gb,
            r.over_cost_gbp,
            r.note
          ]
            .map(escapeCsv)
            .join(',')
        )
      ];
    } else if (healthSection) {
      lines = [
        'iccid,email,stripe_id,plan,operator_name,tariff_name,connection_date,is_barred,note,alt_stripe_id',
        ...healthSection.rows.map((r) =>
          [
            r.iccid,
            r.email,
            r.stripe_id,
            r.plan,
            r.operator_name,
            r.tariff_name,
            r.connection_date,
            r.is_barred,
            r.note,
            r.alt_stripe_id
          ]
            .map(escapeCsv)
            .join(',')
        )
      ];
    } else {
      return;
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selected}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedReport = REPORTS.find((r) => r.key === selected);
  const showActions = healthSection !== null && selected !== 'active';
  const showNote = current !== null && current.rows.some((r) => r.note);
  const visibleHealthRows = healthSection ? healthSection.rows.slice(0, DISPLAY_CAP) : [];
  const visibleUsageRows = usageRows.slice(0, DISPLAY_CAP);

  return (
    <Page>
      <Card>
        <Header>
          <Logo src={logo} alt="Farmstream" />
          <div>
            <Title>SIM health reports</Title>
            <Subtitle>
              Cross-reference Pelion, the activations database and Stripe to monitor the health of
              the subscriber base.
            </Subtitle>
          </div>
        </Header>

        <ReportPills>
          {REPORTS.map((r) => (
            <ReportPill
              key={r.key}
              active={selected === r.key}
              type="button"
              onClick={() => setSelected(r.key)}>
              {r.label}
            </ReportPill>
          ))}
        </ReportPills>
        {selectedReport && <Note>{selectedReport.description}</Note>}
        {usageSelected && (
          <>
            <FilterLabel>
              <input
                type="checkbox"
                checked={h3gOnly}
                onChange={(e) => setH3gOnly(e.target.checked)}
              />{' '}
              H3G Ireland only — these SIMs carry direct per-GB cost
            </FilterLabel>
            <div>
              <BackLink
                onClick={() => {
                  const next = !showEmailPreview;
                  setShowEmailPreview(next);
                  if (next && !emailPreview && staffSession) {
                    fetchEmailPreview({  });
                  }
                }}>
                {showEmailPreview ? 'Hide dunning email preview' : 'Preview dunning emails'}
              </BackLink>
            </div>
            {showEmailPreview && (
              <PreviewPanel>
                {loadingPreview || !emailPreview ? (
                  <Note>Rendering templates…</Note>
                ) : (
                  <>
                    <Note>
                      Blast radius:{' '}
                      {emailPreview.max_recipients > 0
                        ? `top ${emailPreview.max_recipients} by cost (floor £${emailPreview.min_cost_gbp})`
                        : `anyone costing > £${emailPreview.min_cost_gbp}/mo`}{' '}
                      → <strong>{emailPreview.candidate_count}</strong> of{' '}
                      {emailPreview.total_overusers} overusers would be contacted, covering{' '}
                      <strong>£{emailPreview.captured_cost_gbp.toFixed(0)}</strong> of £
                      {emailPreview.total_overuse_cost_gbp.toFixed(0)}/mo overuse cost (
                      {emailPreview.total_overuse_cost_gbp > 0
                        ? Math.round(
                            (emailPreview.captured_cost_gbp /
                              emailPreview.total_overuse_cost_gbp) *
                              100
                          )
                        : 0}
                      %).
                    </Note>
                    {emailPreview.candidates.length > 0 && (
                      <TargetList>
                        {emailPreview.candidates.map((c, i) => (
                          <li key={c.iccid}>
                            {i + 1}. <strong>£{c.over_cost_gbp.toFixed(0)}/mo</strong> —{' '}
                            {c.email} ({c.used_gb.toFixed(0)}GB used of {c.plan_gb.toFixed(0)}
                            GB, {c.operator_name})
                          </li>
                        ))}
                      </TargetList>
                    )}
                    <Note>
                      Templates below use real data from {emailPreview.sample_email}.
                    </Note>
                    <ReportPills>
                      {(
                        [
                          ['stage1', 'Stage 1 — projection warning'],
                          ['stage2', 'Stage 2 — hard limit'],
                          ['barred', 'Suspension notice'],
                          ['reset', 'Plan reset']
                        ] as const
                      ).map(([key, label]) => (
                        <ReportPill
                          key={key}
                          active={previewTemplate === key}
                          type="button"
                          onClick={() => setPreviewTemplate(key)}>
                          {label}
                        </ReportPill>
                      ))}
                    </ReportPills>
                    <div>
                      <Button
                        variant="secondary"
                        onClick={onSendTestEmails}
                        isLoading={sendingTests}>
                        Email me test copies of all four
                      </Button>
                    </div>
                    <EmailFrame
                      dangerouslySetInnerHTML={{
                        __html: emailPreview.templates[previewTemplate]
                      }}
                    />
                  </>
                )}
              </PreviewPanel>
            )}
          </>
        )}

        {selected !== 'dunning' && (
          <>
            <Actions>
              <Button onClick={onRun} isLoading={isLoading}>
                {data ? 'Refresh data' : 'Run reports'}
              </Button>
              {current && current.rows.length > 0 && (
                <Button variant="secondary" onClick={downloadCsv}>
                  {`Download CSV (${current.rows.length})`}
                </Button>
              )}
              {data && (
                <Note>
                  Data from {new Date(data.generated_at).toLocaleTimeString('en-GB')} — switching
                  reports is instant.
                </Note>
              )}
              <BackLink onClick={() => navigate('/admin/select-user')}>← Back to admin</BackLink>
            </Actions>

            {isLoading && (
              <Note>
                Building all four reports: fetching every Pelion subscriber (~4,500), sweeping
                Stripe subscriptions and comparing against the database — this can take up to a
                minute…
              </Note>
            )}
          </>
        )}

        {selected === 'dunning' && (
          <>
            <Actions>
              <Button onClick={refreshDunning} isLoading={loadingDunning}>
                {dunningStatus ? 'Refresh pipeline' : 'Load pipeline'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => onDryRun('warn')}
                isLoading={runningDunning}>
                Dry-run warn
              </Button>
              <Button
                variant="secondary"
                onClick={() => onDryRun('enforce')}
                isLoading={runningDunning}>
                Dry-run enforce
              </Button>
              <BackLink onClick={() => navigate('/admin/select-user')}>← Back to admin</BackLink>
            </Actions>
            {loadingDunning && !dunningStatus && <Note>Loading the pipeline…</Note>}
            {dunningStatus && (
              <>
                <ChipRow>
                  <Chip>
                    {dunningStatus.month} · day {dunningStatus.today_day}
                  </Chip>
                  <Chip>
                    {dunningStatus.config.max_recipients > 0
                      ? `Cap ${dunningStatus.config.max_recipients}/month`
                      : `Targets anyone costing > £${dunningStatus.config.min_cost_gbp}/mo`}
                  </Chip>
                  <Chip>
                    Hard limit {dunningStatus.config.hard_limit_gb.toFixed(0)}GB +{' '}
                    {dunningStatus.config.grace_gb.toFixed(0)}GB final grace, then barred until
                    the 1st
                  </Chip>
                  <Chip warn={!dunningStatus.config.auto_bar}>
                    {dunningStatus.config.auto_bar
                      ? 'Auto-bar ON'
                      : 'Auto-bar OFF — barring is manual'}
                  </Chip>
                  <ScopeToggle>
                    <ScopeOption
                      active={dunningStatus.config.scope === 'h3g'}
                      disabled={settingScope}
                      type="button"
                      onClick={() => onSetScope('h3g')}>
                      H3G SIMs only
                    </ScopeOption>
                    <ScopeOption
                      active={dunningStatus.config.scope === 'all'}
                      disabled={settingScope}
                      type="button"
                      onClick={() => onSetScope('all')}>
                      All SIMs
                    </ScopeOption>
                  </ScopeToggle>
                </ChipRow>
                <SummaryRow>
                  <SummaryTile>
                    <SummaryValue>
                      {dunningStatus.config.max_recipients > 0
                        ? `${dunningStatus.slots_used}/${dunningStatus.config.max_recipients}`
                        : dunningStatus.slots_used}
                    </SummaryValue>
                    <SummaryLabel>In pipeline this month</SummaryLabel>
                  </SummaryTile>
                  <SummaryTile>
                    <SummaryValue>
                      {dunningStatus.pipeline.filter((p) => p.stage === 'warned').length}
                    </SummaryValue>
                    <SummaryLabel>Stage 1 warned</SummaryLabel>
                  </SummaryTile>
                  <SummaryTile
                    highlight={dunningStatus.pipeline.some((p) => p.stage === 'followup')}>
                    <SummaryValue>
                      {dunningStatus.pipeline.filter((p) => p.stage === 'followup').length}
                    </SummaryValue>
                    <SummaryLabel>At hard limit</SummaryLabel>
                  </SummaryTile>
                  <SummaryTile
                    highlight={dunningStatus.pipeline.some((p) => p.stage === 'bar_pending')}>
                    <SummaryValue>
                      {dunningStatus.pipeline.filter((p) => p.stage === 'bar_pending').length}
                    </SummaryValue>
                    <SummaryLabel>Bar pending</SummaryLabel>
                  </SummaryTile>
                  <SummaryTile>
                    <SummaryValue>
                      {dunningStatus.pipeline.filter((p) => p.stage === 'barred').length}
                    </SummaryValue>
                    <SummaryLabel>Barred until reset</SummaryLabel>
                  </SummaryTile>
                </SummaryRow>

                {dunningStatus.pipeline.length === 0 ? (
                  <Note>
                    Nobody has been contacted yet this month — run the warn mode (or wait for the
                    schedule) and the pipeline will fill up here.
                  </Note>
                ) : (
                  (
                    [
                      ['warned', '📨 Stage 1 — projection warning sent'],
                      ['followup', '⚠️ Stage 2 — hard limit reached'],
                      ['bar_pending', '🚫 Bar pending — needs your click'],
                      ['barred', '⛔ Barred until the 1st'],
                      ['unbarred', '✅ Unbarred at reset']
                    ] as const
                  ).map(([stage, title]) => {
                    const items = dunningStatus.pipeline.filter((p) => p.stage === stage);
                    if (items.length === 0) return null;
                    return (
                      <StageSection key={stage}>
                        <StageTitle>{title}</StageTitle>
                        {items.map((p) => (
                          <PipelineCard key={p.iccid} urgent={p.stage === 'bar_pending'}>
                            <div>
                              <strong>{p.email || '—'}</strong>{' '}
                              <CardMono>{p.iccid}</CardMono>
                            </div>
                            <CardMeta>
                              £{p.over_cost_gbp.toFixed(0)}/mo · plan {p.plan_gb.toFixed(0)}GB ·{' '}
                              {p.snapshot_gb.toFixed(1)}GB at warning →{' '}
                              {p.current_gb !== null ? `${p.current_gb.toFixed(1)}GB now` : 'n/a'}
                            </CardMeta>
                            <CardMeta>
                              {p.sent_at ? `${p.stage} ${p.sent_at} (${p.days_since}d ago)` : p.stage}
                              {p.stage === 'warned' && p.remaining_gb !== null && (
                                <VerdictBadge good>
                                  {`${p.remaining_gb.toFixed(1)}GB to the ${p.hard_limit_gb.toFixed(0)}GB limit`}
                                </VerdictBadge>
                              )}
                              {p.stage === 'followup' && (
                                <VerdictBadge good={!p.over_final}>
                                  {p.over_final
                                    ? 'FINAL LIMIT EXCEEDED'
                                    : `${(p.remaining_gb ?? 0).toFixed(2)}GB final grace left`}
                                </VerdictBadge>
                              )}
                            </CardMeta>
                            {p.stage === 'bar_pending' && (
                              <div>
                                <ActionBtn danger onClick={() => onDashboardBar(p.iccid)}>
                                  Bar until reset
                                </ActionBtn>
                              </div>
                            )}
                          </PipelineCard>
                        ))}
                      </StageSection>
                    );
                  })
                )}

                {dunningStatus.watching.length > 0 && (
                  <StageSection>
                    <StageTitle>
                      👀 Watching —{' '}
                      {dunningStatus.config.max_recipients > 0
                        ? `would fill the remaining ${
                            dunningStatus.config.max_recipients - dunningStatus.slots_used
                          } slot(s) on the next warn run`
                        : `over the £${dunningStatus.config.min_cost_gbp} floor, contacted on the next warn run`}
                    </StageTitle>
                    {dunningStatus.watching.map((w) => (
                      <PipelineCard key={w.iccid}>
                        <div>
                          <strong>{w.email || '—'}</strong> <CardMono>{w.iccid}</CardMono>
                        </div>
                        <CardMeta>
                          £{w.over_cost_gbp.toFixed(0)}/mo · {w.used_gb.toFixed(1)}GB used of{' '}
                          {w.plan_gb.toFixed(0)}GB · projected {w.projected_gb.toFixed(1)}GB ·{' '}
                          {w.operator_name}
                        </CardMeta>
                      </PipelineCard>
                    ))}
                  </StageSection>
                )}
              </>
            )}
          </>
        )}

        {current && (
          <>
            <SummaryRow>
              {current.tiles.map((t) => (
                <SummaryTile key={t.label} highlight={Boolean(t.highlight)}>
                  <SummaryValue>{t.value}</SummaryValue>
                  <SummaryLabel>{t.label}</SummaryLabel>
                </SummaryTile>
              ))}
            </SummaryRow>

            {current.rows.length === 0 ? (
              <Note>Nothing flagged — this report is clean. 🎉</Note>
            ) : (
              <>
                {current.rows.length > DISPLAY_CAP && (
                  <Note>
                    Showing the top {DISPLAY_CAP} of {current.rows.length} rows — the CSV download
                    contains the full list.
                  </Note>
                )}
                {usageSection ? (
                  <TableWrapper>
                    <table>
                      <thead>
                        <tr>
                          <th>ICCID</th>
                          <th>Email</th>
                          <th>Device</th>
                          <th>Operator</th>
                          <th>State</th>
                          <th>Plan GB</th>
                          <th>Used GB</th>
                          <th>Projected EoM</th>
                          <th>% of plan</th>
                          <th>Over cost £</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleUsageRows.map((r) => (
                          <tr key={r.iccid}>
                            <td className="mono">{r.iccid}</td>
                            <td>{r.email || '—'}</td>
                            <td>{r.device || '—'}</td>
                            <td>{r.operator_name || '—'}</td>
                            <td>{r.sim_state}</td>
                            <td>{r.plan_gb > 0 ? r.plan_gb.toFixed(0) : '—'}</td>
                            <td>{r.used_gb.toFixed(2)}</td>
                            <td>{r.projected_gb.toFixed(2)}</td>
                            <td>{r.pct_used !== null ? `${r.pct_used.toFixed(0)}%` : '—'}</td>
                            <td>{r.over_cost_gbp > 0 ? `£${r.over_cost_gbp.toFixed(2)}` : '—'}</td>
                            <td>{r.note || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrapper>
                ) : (
                  <TableWrapper>
                    <table>
                      <thead>
                        <tr>
                          <th>ICCID</th>
                          <th>Email</th>
                          <th>Stripe ID</th>
                          <th>Plan</th>
                          <th>Operator</th>
                          <th>Activated</th>
                          <th>Barred</th>
                          {showNote && <th>Note</th>}
                          {showActions && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHealthRows.map((r) => (
                          <tr key={r.iccid}>
                            <td className="mono">{r.iccid}</td>
                            <td>{r.email || '—'}</td>
                            <td className="mono">{r.stripe_id || '—'}</td>
                            <td>{r.plan || '—'}</td>
                            <td>{r.operator_name || '—'}</td>
                            <td>{r.connection_date ? r.connection_date.slice(0, 10) : '—'}</td>
                            <td>{r.is_barred ? 'Yes' : 'No'}</td>
                            {showNote && <td>{r.note || '—'}</td>}
                            {showActions && (
                              <td>
                                <RowActions>
                                  <ActionBtn
                                    disabled={busyIccid === r.iccid}
                                    onClick={() =>
                                      onSimAction(r.iccid, r.is_barred ? 'unbar' : 'bar')
                                    }>
                                    {busyIccid === r.iccid ? '…' : r.is_barred ? 'Unbar' : 'Bar'}
                                  </ActionBtn>
                                  <ActionBtn
                                    danger
                                    armed={armedTerminate === r.iccid}
                                    disabled={busyIccid === r.iccid}
                                    onClick={() => onSimAction(r.iccid, 'terminate')}>
                                    {busyIccid === r.iccid
                                      ? '…'
                                      : armedTerminate === r.iccid
                                        ? 'Confirm?'
                                        : 'Terminate'}
                                  </ActionBtn>
                                </RowActions>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrapper>
                )}
              </>
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gray};
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 76rem;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 850px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Logo = styled.img`
  width: 4rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.black};
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
`;

const ReportPills = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ReportPill = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid
    ${({ theme, active }) => (active ? theme.colors.primary : `${theme.colors.gray500}40`)};
  background: ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.white)};
  color: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.gray900)};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ScopeToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.primary}60;
  border-radius: 1rem;
  overflow: hidden;
`;

const ScopeOption = styled.button<{ active: boolean }>`
  padding: 0.25rem 0.8rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.white)};
  color: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.gray900)};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ChipRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Chip = styled.span<{ warn?: boolean }>`
  padding: 0.25rem 0.7rem;
  border-radius: 1rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 600;
  background: ${({ theme, warn }) => (warn ? `${theme.colors.yellow900}20` : `${theme.colors.gray}60`)};
  border: 1px solid
    ${({ theme, warn }) => (warn ? theme.colors.yellow900 : `${theme.colors.gray500}40`)};
  color: ${({ theme }) => theme.colors.black};
`;

const StageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StageTitle = styled.h3`
  margin: 0.5rem 0 0;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.black};
`;

const PipelineCard = styled.div<{ urgent?: boolean }>`
  border: 1px solid
    ${({ theme, urgent }) => (urgent ? theme.colors.red : `${theme.colors.gray500}30`)};
  background: ${({ theme, urgent }) => (urgent ? `${theme.colors.red}08` : theme.colors.white)};
  border-radius: 0.6rem;
  padding: 0.6rem 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: ${({ theme }) => theme.fontSize.small};
`;

const CardMono = styled.span`
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-left: 0.4rem;
`;

const CardMeta = styled.div`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.gray900};
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const VerdictBadge = styled.span<{ good: boolean }>`
  padding: 0.1rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.65rem;
  font-weight: 700;
  background: ${({ theme, good }) => (good ? `${theme.colors.green900}15` : `${theme.colors.red}12`)};
  color: ${({ theme, good }) => (good ? theme.colors.green900 : theme.colors.red)};
  border: 1px solid
    ${({ theme, good }) => (good ? `${theme.colors.green900}50` : `${theme.colors.red}50`)};
`;

const TargetList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.black};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  list-style: none;
`;

const PreviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  border-radius: 0.75rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.gray}20;
`;

const EmailFrame = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  border-radius: 0.5rem;
  padding: 1.25rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  line-height: 1.5;
  max-width: 40rem;

  table td {
    font-size: ${({ theme }) => theme.fontSize.small};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
  cursor: pointer;
  user-select: none;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.small};
  cursor: pointer;
  text-decoration: underline;
`;

const Note = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 1rem;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryTile = styled.div<{ highlight?: boolean }>`
  border: 1px solid
    ${({ theme, highlight }) => (highlight ? theme.colors.red : `${theme.colors.gray500}30`)};
  background: ${({ theme, highlight }) =>
    highlight ? `${theme.colors.red}08` : `${theme.colors.gray}30`};
  border-radius: 0.75rem;
  padding: 1rem;
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.gray900};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
`;

const ActionBtn = styled.button<{ danger?: boolean; armed?: boolean }>`
  padding: 0.3rem 0.6rem;
  border-radius: 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid
    ${({ theme, danger }) => (danger ? theme.colors.red : `${theme.colors.primary}60`)};
  background: ${({ theme, danger, armed }) =>
    armed ? theme.colors.red : danger ? `${theme.colors.red}10` : theme.colors.white};
  color: ${({ theme, danger, armed }) =>
    armed ? theme.colors.white : danger ? theme.colors.red : theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${({ theme, danger, armed }) =>
      armed ? theme.colors.red : danger ? `${theme.colors.red}20` : `${theme.colors.primary}10`};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-height: 34rem;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 0.75rem;

  table {
    width: 100%;
    border-spacing: 0;
    font-size: ${({ theme }) => theme.fontSize.extraSmall};
    min-width: 62rem;
  }

  th {
    text-align: left;
    padding: 0.6rem 0.75rem;
    background: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.black};
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.7rem;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid ${({ theme }) => theme.colors.gray500}15;
  }

  td.mono {
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    white-space: nowrap;
  }

  tr:hover td {
    background: ${({ theme }) => theme.colors.primary}06;
  }
`;
